import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, X } from 'lucide-react';

export const CLASS_COLORS = [
  '#002FA7', '#34C759', '#FF9500', '#AF52DE', '#FF3B30', '#007AFF',
  '#5856D6', '#FF2D55', '#4B5563', '#059669',
];

const DATATYPES = ['string', 'integer', 'number', 'boolean', 'date', 'url'];

const ClassFormDialog = ({ open, initial, parent, allClasses, onCancel, onSubmit }) => {
  const [form, setForm] = useState({
    class_id: initial.class_id,
    name: initial.name || '',
    description: initial.description || '',
    parent_class_id: initial.parent_class_id || parent?.class_id || null,
    color: initial.color || CLASS_COLORS[0],
    icon: initial.icon || 'Box',
    properties: initial.properties || [],
    synonyms: initial.synonyms || [],
  });
  const [propInput, setPropInput] = useState({ name: '', datatype: 'string', description: '', required: false });
  const [synInput, setSynInput] = useState('');

  const addProp = () => {
    if (!propInput.name.trim()) return;
    setForm({ ...form, properties: [...form.properties, { ...propInput }] });
    setPropInput({ name: '', datatype: 'string', description: '', required: false });
  };

  const removeProp = (idx) => setForm({ ...form, properties: form.properties.filter((_, i) => i !== idx) });
  const removeSyn = (idx) => setForm({ ...form, synonyms: form.synonyms.filter((_, i) => i !== idx) });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{form.class_id ? 'Edit Class' : parent ? `New Child of ${parent.name}` : 'New Class'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Name *</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Methodology" data-testid="class-name-input" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Parent Class</Label>
              <Select value={form.parent_class_id || 'none'} onValueChange={(v) => setForm({ ...form, parent_class_id: v === 'none' ? null : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">(root class)</SelectItem>
                  {allClasses.filter(c => c.class_id !== form.class_id).map(c => (
                    <SelectItem key={c.class_id} value={c.class_id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex gap-1 mt-1 flex-wrap">
                {CLASS_COLORS.map(c => (
                  <button key={c} type="button" onClick={() => setForm({ ...form, color: c })}
                    className={`w-7 h-7 rounded ${form.color === c ? 'ring-2 ring-offset-1 ring-[#111827]' : ''}`} style={{ backgroundColor: c }} />
                ))}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">Properties</Label>
            <div className="flex gap-1 mt-1">
              <Input className="h-8 text-xs" placeholder="name" value={propInput.name} onChange={(e) => setPropInput({ ...propInput, name: e.target.value })} />
              <Select value={propInput.datatype} onValueChange={(v) => setPropInput({ ...propInput, datatype: v })}>
                <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                <SelectContent>{DATATYPES.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" size="sm" variant="outline" className="h-8" onClick={addProp}><Plus className="w-3.5 h-3.5" /></Button>
            </div>
            {form.properties.length > 0 && (
              <div className="flex gap-1 flex-wrap mt-1.5">
                {form.properties.map((p, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] gap-1">
                    {p.name}:{p.datatype}
                    <button type="button" onClick={() => removeProp(i)}><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Synonyms</Label>
            <Input className="h-8 text-xs mt-1" value={synInput} onChange={(e) => setSynInput(e.target.value)} onKeyDown={(e) => {
              if (e.key === 'Enter' && synInput.trim()) {
                e.preventDefault();
                setForm({ ...form, synonyms: [...form.synonyms, synInput.trim()] });
                setSynInput('');
              }
            }} placeholder="add and press Enter" />
            {form.synonyms.length > 0 && (
              <div className="flex gap-1 flex-wrap mt-1.5">
                {form.synonyms.map((s, i) => (
                  <Badge key={i} variant="secondary" className="text-[10px] gap-1">{s}<button type="button" onClick={() => removeSyn(i)}><X className="w-3 h-3" /></button></Badge>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button className="bg-[#002FA7] hover:bg-[#001F6F]" onClick={() => onSubmit(form)} disabled={!form.name.trim()} data-testid="submit-class">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ClassFormDialog;
